import { Trash2, BookmarkPlus, ArrowLeftRight, RotateCcw, Check, X } from "lucide-react";
import { KINDS } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { TimeInput } from "@/components/TimeInput";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { SignForm } from "@/components/SignForm";
import { RouteLanes, SignDirection } from "@/components/DirectionQuestions";

const F = ({ label, children, className = "" }) => (
  <div className={`space-y-1 ${className}`}>
    <Label className="text-[0.6875rem] text-slate-500">{label}</Label>
    {children}
  </div>
);

const Toggle = ({ label, hint, checked, onChange, testid }) => (
  <label className="flex items-start justify-between gap-3 py-1.5 cursor-pointer">
    <span>
      <span className="text-xs text-slate-800 block">{label}</span>
      {hint && <span className="text-[0.6875rem] text-slate-500">{hint}</span>}
    </span>
    <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
  </label>
);

const COVER = [
  { v: "senal", label: "Solo vallas y señal", patch: { staff_mode: "senal" } },
  { v: "custodia", label: "Protección Civil / auxiliar", patch: { staff_mode: "", regulation: false } },
  { v: "agente", label: "Agente de Policía Local", patch: { staff_mode: "", regulation: true } },
];

const AutoCutBox = ({ el, onChange, onReject, readOnly }) => {
  const cur = el.staff_mode === "senal" ? "senal" : el.regulation ? "agente" : "custodia";
  return (
    <div className="space-y-2" data-testid="auto-cut-box">
      <div className="text-[0.6875rem] text-rose-800 bg-rose-500/10 border border-rose-500/30 rounded-lg p-2.5">
        <b>Corte previo {el.locked ? "aceptado" : "propuesto"}</b> · {el.auto_reason}. {el.locked ? "Cuenta en el plan y no se recalcula." : "No cuenta en el plan hasta que lo acepte. Si lo mueve o edita, se acepta."}
      </div>
      {!el.locked && !readOnly && (
        <div className="grid grid-cols-2 gap-2">
          <Button type="button" size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950" onClick={() => onChange({ ...el, locked: true })} data-testid="auto-cut-accept-button"><Check className="w-4 h-4 mr-1" /> Aceptar</Button>
          <Button type="button" size="sm" variant="outline" className="text-red-700" onClick={onReject} data-testid="auto-cut-reject-button"><X className="w-4 h-4 mr-1" /> Rechazar</Button>
        </div>
      )}
      <Label className="text-[0.6875rem] text-slate-500">Cubrir con</Label>
      <div className="grid grid-cols-3 gap-1" data-testid="auto-cut-cover">
        {COVER.map((c) => (
          <button key={c.v} type="button" onClick={() => onChange({ ...el, ...c.patch, locked: true })} data-testid={`auto-cut-cover-${c.v}`}
            className={`text-[0.6875rem] px-2 py-1.5 rounded-md border transition-colors ${cur === c.v ? "border-rose-400 bg-rose-500/20 text-rose-800" : "border-slate-200 text-slate-700 hover:bg-slate-100"}`}>{c.label}</button>
        ))}
      </div>
      {el.locked && (
        <Button type="button" variant="ghost" size="sm" className="h-7 text-xs text-slate-700" onClick={() => onChange({ ...el, locked: false, staff_mode: "senal", regulation: false })} data-testid="auto-cut-unlock-button">
          <RotateCcw className="w-3.5 h-3.5 mr-1" /> Volver a propuesta
        </Button>
      )}
    </div>
  );
};

export const ElementForm = ({ el, onChange, onDelete, readOnly, timing, onSaveTemplate }) => {
  const lock = el.auto_generated ? { locked: true } : {};
  const set = (k, num) => (e) => onChange({ ...el, [k]: num ? Number(e.target.value) : e.target.value, ...lock });
  const setB = (k) => (v) => onChange({ ...el, [k]: v, ...lock });
  const isCut = el.kind === "cut_point" || el.kind === "street_cut";
  return (
    <fieldset disabled={readOnly} className="space-y-3 rise" data-testid="element-form">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[0.625rem] tracking-widest uppercase" style={{ color: KINDS[el.kind].color }}>{KINDS[el.kind].label}</span>
        {!readOnly && (
          <Button size="sm" variant="ghost" className="h-7 text-red-700 hover:text-red-800" onClick={onDelete} data-testid="element-delete-button">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
          </Button>
        )}
      </div>
      <F label="Nombre / ubicación"><Input value={el.name || ""} onChange={set("name")} className="h-9" data-testid="element-name-input" /></F>

      {el.auto_generated && <AutoCutBox el={el} onChange={onChange} onReject={onDelete} readOnly={readOnly} />}
      {el.kind === "cut_point" && !el.auto_generated && <SignDirection el={el} onChange={onChange} readOnly={readOnly} />}

      {el.kind === "route" && <RouteLanes el={el} onChange={onChange} readOnly={readOnly} />}
      {el.kind === "route" && (
        <div className="grid grid-cols-3 gap-2">
          <F label="Hora salida"><TimeInput value={el.start_time || ""} onChange={set("start_time")} className="h-9" data-testid="route-start-input" /></F>
          <F label="Velocidad km/h"><Input type="number" step="0.5" min="0.5" value={el.speed_kmh ?? 4} onChange={set("speed_kmh", true)} className="h-9" data-testid="route-speed-input" /></F>
          <F label="Paso (min)"><Input type="number" min="1" value={el.passage_min ?? 20} onChange={set("passage_min", true)} className="h-9" data-testid="route-passage-input" /></F>
          <div className="col-span-3 text-[0.6875rem] text-slate-500">Paso = minutos que tarda toda la comitiva en pasar por un punto. Carrera ≈ 10-15 km/h · Procesión ≈ 1-2 km/h.</div>
          <div className="col-span-3"><Toggle label="Escolta de apertura y cierre" hint="Motos / vehículos acompañando la comitiva" checked={el.escort} onChange={setB("escort")} testid="route-escort-switch" /></div>
          {el.escort && <F label="Agentes por escolta"><Input type="number" min="1" value={el.escort_agents ?? 2} onChange={set("escort_agents", true)} className="h-9" data-testid="route-escort-agents-input" /></F>}
        </div>
      )}

      {el.kind === "special_path" && (
        <div className="space-y-2">
          <div className="text-[0.6875rem] text-purple-700 bg-purple-500/10 border border-purple-500/30 rounded-lg p-2.5">Vía no habitual que puede abrirse al tráfico. El plan la usará si mejora o hace posible un desvío, solo dentro de su horario.</div>
          <div className="grid grid-cols-2 gap-2">
            <F label="Habilitable desde"><TimeInput value={el.available_from || ""} onChange={set("available_from")} className="h-9" data-testid="special-from-input" /></F>
            <F label="Hasta"><TimeInput value={el.available_to || ""} onChange={set("available_to")} className="h-9" data-testid="special-to-input" /></F>
          </div>
          <div className="text-[0.625rem] text-slate-500">Vacío = disponible durante todo el evento. El desvío solo la usa si el corte completo cae dentro de este horario.</div>
          <Toggle label="Asignar personal para habilitar el paso" hint="Retirar bolardos/vallas y regular la entrada" checked={el.habilitation_staff} onChange={setB("habilitation_staff")} testid="special-staff-switch" />
          {el.habilitation_staff && (
            <F label="Agentes / auxiliares necesarios"><Input type="number" min="1" value={el.staff_required ?? 1} onChange={set("staff_required", true)} className="h-9" data-testid="special-staff-input" /></F>
          )}
        </div>
      )}

      {el.kind === "reversed_oneway" && (
        <div className="space-y-2">
          <div className="text-[0.6875rem] text-pink-700 bg-pink-500/10 border border-pink-500/30 rounded-lg p-2.5">Calle de sentido único cuyo sentido se invierte provisionalmente. Las flechas rosas indican el NUEVO sentido. El plan la usará en los desvíos si acorta el recorrido y no avisará de contra sentido en ella.</div>
          <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => onChange({ ...el, coords: [...el.coords].reverse() })} data-testid="reversed-flip-button">
            <ArrowLeftRight className="w-4 h-4 mr-1" /> Cambiar dirección
          </Button>
          <div className="grid grid-cols-2 gap-2">
            <F label="Invertido desde"><TimeInput value={el.available_from || ""} onChange={set("available_from")} className="h-9" data-testid="reversed-from-input" /></F>
            <F label="Hasta"><TimeInput value={el.available_to || ""} onChange={set("available_to")} className="h-9" data-testid="reversed-to-input" /></F>
          </div>
          <div className="text-[0.625rem] text-slate-500">Vacío = todo el evento. Los desvíos solo la usan si el corte completo cae dentro de este horario.</div>
          {timing && <div className="font-mono text-[0.6875rem] text-emerald-700" data-testid="element-computed-timing">Plan: {timing.start_hm} – {timing.end_hm} ({timing.source})</div>}
          <Toggle label="Agentes en ambos extremos" hint="Regular la entrada y la salida del tramo invertido" checked={el.reversal_staff} onChange={setB("reversal_staff")} testid="reversed-staff-switch" />
          {el.reversal_staff && (
            <F label="Agentes por extremo"><Input type="number" min="1" value={el.staff_required ?? 1} onChange={set("staff_required", true)} className="h-9" data-testid="reversed-staff-input" /></F>
          )}
        </div>
      )}

      {el.kind === "sign" && <SignForm el={el} onChange={onChange} readOnly={readOnly} timing={timing} />}

      {!["route", "special_path", "reversed_oneway", "sign"].includes(el.kind) && (
        <>
          {!el.auto_generated && <Toggle label="Horario automático" hint="Calcula cierre/apertura según el paso de la comitiva" checked={el.auto_time} onChange={setB("auto_time")} testid="element-auto-time-switch" />}
          {!el.auto_time && (
            <div className="grid grid-cols-2 gap-2">
              <F label={el.kind === "no_parking" ? "Prohibido desde" : "Corte desde"}><TimeInput value={el.start_time || ""} onChange={set("start_time")} className="h-9" data-testid="element-start-input" /></F>
              <F label="Hasta"><TimeInput value={el.end_time || ""} onChange={set("end_time")} className="h-9" data-testid="element-end-input" /></F>
            </div>
          )}
          {timing && <div className="font-mono text-[0.6875rem] text-emerald-700" data-testid="element-computed-timing">Plan: {timing.start_hm} – {timing.end_hm} ({timing.source})</div>}
          {(!el.auto_generated || el.staff_mode !== "senal") && <F label={el.kind === "street_cut" ? "Personas por extremo" : el.kind === "no_parking" ? "Agentes/auxiliares para grúa" : "Agentes necesarios"}>
            <Input type="number" min="1" value={el.staff_required ?? 1} onChange={set("staff_required", true)} className="h-9" data-testid="element-staff-input" />
          </F>}
        </>
      )}

      {isCut && !el.auto_generated && (
        <div className="border-t border-slate-200 pt-2">
          <Toggle label="Requiere regulación policial" hint="Si no, puede cubrirlo Protección Civil u organización" checked={el.regulation} onChange={setB("regulation")} testid="element-regulation-switch" />
          <Toggle label="Calcular desvío" checked={el.detour} onChange={setB("detour")} testid="element-detour-switch" />
          {el.detour && <Toggle label="Agente en el inicio del desvío" checked={el.detour_staff} onChange={setB("detour_staff")} testid="element-detour-staff-switch" />}
        </div>
      )}
      {isCut && (
        <Toggle label="Excepto residentes" hint="Permite el acceso a garajes y vecinos · añade panel S-860" checked={el.residents_only} onChange={setB("residents_only")} testid="element-residents-switch" />
      )}
      <F label="Notas"><Textarea rows={2} value={el.notes || ""} onChange={set("notes")} data-testid="element-notes-input" /></F>
      {onSaveTemplate && !readOnly && (
        <Button type="button" variant="outline" size="sm" className="w-full" onClick={onSaveTemplate} disabled={!el.name?.trim()} data-testid="save-as-template-button">
          <BookmarkPlus className="w-4 h-4 mr-1" /> Guardar como plantilla
        </Button>
      )}
    </fieldset>
  );
};
